import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { BellRing } from "lucide-react";
import {
  dismissToneAlert,
  toneSlotLabel,
  useToneAlertBatch,
} from "./toneAlert";

const SHOWN_NAMES_CAP = 6;

/**
 * Global popup for stage/status notification tones. Mounted once in
 * AppProviders; shows whatever batch the coalescer flushed — a single lead
 * with its status, or "N leads" for a bulk sweep — and stays until dismissed.
 */
export default function ToneAlertHost() {
  const batch = useToneAlertBatch();
  if (!batch?.length) return null;

  const single = batch.length === 1 ? batch[0] : null;
  const singleSlot = single ? toneSlotLabel(single) : undefined;
  const names = [
    ...new Set(
      batch
        .map((b) => b.leadName?.trim())
        .filter((n): n is string => Boolean(n)),
    ),
  ];

  return (
    <Dialog open onOpenChange={(open) => !open && dismissToneAlert()}>
      <DialogContent className="sm:max-w-[440px] rounded-xl">
        <DialogHeader>
          <p className="flex items-center gap-2 text-base font-semibold text-[#051912]">
            <span className="flex size-8 items-center justify-center rounded-full bg-[#eaf5f1]">
              <BellRing className="size-4 text-[#218760]" />
            </span>
            {single
              ? single.kind === "assignment"
                ? `New ${single.childId != null ? "deal" : "lead"} for you`
                : "Lead update"
              : `${batch.length} leads need your attention`}
          </p>
          <Separator />
          <DialogDescription className="pt-2 text-[13px] text-[#505e59]" asChild>
            <div>
              {single ? (
                <p className="m-0">
                  <span className="font-semibold text-[#1e302a]">
                    {single.leadName || "A lead"}
                  </span>
                  {singleSlot ? (
                    <>
                      {" "}is now in{" "}
                      <span className="font-medium">{singleSlot}</span>
                    </>
                  ) : (
                    " was updated"
                  )}
                  {single.kind === "assignment"
                    ? " and has been assigned to you."
                    : "."}
                </p>
              ) : (
                <div>
                  <p className="m-0 mb-2">
                    {batch.length} leads were assigned to you or moved into a
                    status you follow:
                  </p>
                  <ul className="m-0 list-disc pl-5 space-y-0.5">
                    {names.slice(0, SHOWN_NAMES_CAP).map((n) => (
                      <li key={n}>{n}</li>
                    ))}
                    {names.length > SHOWN_NAMES_CAP && (
                      <li className="list-none text-[#697571]">
                        +{names.length - SHOWN_NAMES_CAP} more
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4 flex justify-end">
          <Button variant="primary" onClick={dismissToneAlert}>
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
